/**
 * @module engine3d/solid/SolidEngine
 * @summary Lifecycle, camera, input and the imperative API for the part
 *   modeller — the Solid view's counterpart to `Engine3D`.
 * @description
 *   Deliberately the same shape as the two engines already here: mount onto a
 *   canvas, run a render-on-demand loop, expose commands to React, and hold the
 *   document itself rather than letting React own it.
 *
 *   What it does NOT do is touch the PFD document. The part modeller is a
 *   separate workspace with its own document (a `Part`), its own undo stack and
 *   its own autosave key. Nothing in here reads or writes `S.shapes`, so no
 *   amount of modelling can affect a drawing. Sending a finished part into the
 *   plant view as a symbol model is the natural next step and is deliberately
 *   NOT wired up yet — it would mean writing to the shared model registry, which
 *   is a decision to make on purpose rather than as a side effect of a demo.
 *
 *   Known limits, stated rather than hidden:
 *     • No boolean cuts — see the note in `part.ts`.
 *     • Selection is one feature at a time.
 */
import { Camera, wheelZoomFactor, type ProjectionKind, type ViewPreset } from '../camera';
import { deg, rayPlanePoint, v3, type Vec2, type Vec3 } from '../math3';
import { Renderer3D, type RenderOptions } from '../renderer3d';
import { emptyScene, type Scene } from '../scene';
import { buildPart, type PartStats } from './build';
import {
  emptyPart,
  KINDS,
  mkFeature,
  nextId,
  syncSeq,
  uniqueName,
  type Feature,
  type FeatureKind,
  type Part,
  type Pattern,
} from './part';
import { partFromTemplate } from './presets';
import type { Sketch } from './part';
import { sceneToStl } from './stl';
import type { MatKey } from '../materials';
import { dlBlob, dlText } from '../../lib/download';
import { toast } from '../../lib/toast';

/**
 * Where the work-in-progress part is kept between sessions.
 * Bumped to v2 when the mode stopped opening on a template: a v1 entry holds
 * the sample hopper the modeller used to seed itself, and restoring that would
 * defeat the empty page it now opens on.
 */
const AUTOSAVE_KEY = 'jcc-pfd-solid-part-v2';

export interface SolidStats extends PartStats {
  fps: number;
}

export type SolidListener = () => void;

/** Snap steps offered in the HUD, in millimetres. */
export const SNAPS = [0, 1, 5, 10, 25, 50, 100];

/**
 * A sketch in progress.
 *
 * The camera is locked normal to the plane while this is live — orbiting away
 * from a sketch plane is how you draw a profile that is not the profile you
 * thought you were drawing, so the gesture is simply not offered until the
 * sketch is finished or abandoned.
 */
export interface SketchState {
  plane: Sketch['plane'];
  op: Sketch['op'];
  /** Points so far, in millimetres relative to `origin`. */
  pts: Vec2[];
  /** Feature being re-opened, or null for a new one. */
  editing: string | null;
  /** Where the sketch plane sits, in millimetres. */
  origin: Vec3;
  /** Snapped cursor position, for the rubber band. */
  preview: Vec2 | null;
  /** Cursor is over the first point — the next click closes the outline. */
  closing: boolean;
}

/** How each sketch plane is named, and which way the solid it makes grows. */
const PLANE_VIEW: Record<Sketch['plane'], { label: string; grows: string }> = {
  xz: { label: 'Plan (X-Z)', grows: 'upward' },
  xy: { label: 'Front (X-Y)', grows: 'toward the viewer' },
  zy: { label: 'Side (Z-Y)', grows: 'to the right' },
};

export { PLANE_VIEW };

class SolidEngine {
  private canvas: HTMLCanvasElement | null = null;
  private wrap: HTMLElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ro: ResizeObserver | null = null;
  private raf = 0;
  private mounted = false;
  private detachers: Array<() => void> = [];

  private renderer = new Renderer3D();
  private scene: Scene = emptyScene();
  private stats: PartStats = { features: 0, instances: 0, triangles: 0, size: v3() };
  private dirty = true;
  private partDirty = true;
  private fps = 0;
  private lastFrame = 0;
  private listeners = new Set<SolidListener>();

  readonly camera = new Camera();

  /** The document. */
  private part: Part = emptyPart();
  private selId: string | null = null;
  private hoverId: string | null = null;
  private sketch: SketchState | null = null;

  /** Undo/redo, as whole-part snapshots — a part is small enough that this is
   *  simpler and more reliable than a command log, and never drifts. */
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  /** Coalesces a slider drag into one undo entry instead of forty. */
  private coalesceKey = '';
  private coalesceTimer = 0;

  /** View toggles surfaced in the HUD. */
  opts: Omit<RenderOptions, 'selected' | 'hoveredId'> = {
    grid: true,
    shadows: true,
    labels: false,
    tags: false,
    floors: false,
    isolate: false,
    dims: false,
    dimsSelectedOnly: true,
    scaleMm: 25,
    heightScale: 1,
  };

  /** Placement snap, in millimetres. 0 is off. */
  snapMm = 25;

  // ── Lifecycle ──────────────────────────────────────────────────────────

  mount(canvas: HTMLCanvasElement, wrap: HTMLElement): void {
    if (this.mounted) this.unmount();
    this.canvas = canvas;
    this.wrap = wrap;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) this.renderer.attach(this.ctx);
    this.mounted = true;

    // Solid opens on an EMPTY page. Only a part the user made themselves comes
    // back — never a sample. Seeding the mode with a template made the first
    // frame look impressive and the first action confusing: you had to clear
    // somebody else's hopper before you could draw your own.
    if (!this.part.features.length) this.restore();

    this.resize();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(wrap);
    window.addEventListener('resize', this.onWindowResize);
    this.attachInput(canvas);
    this.rebuild();
    this.fitView();
    this.loop();
  }

  unmount(): void {
    if (!this.mounted) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.detachers.forEach((d) => d());
    this.detachers = [];
    this.ro?.disconnect();
    this.ro = null;
    window.removeEventListener('resize', this.onWindowResize);
    window.clearTimeout(this.qualityTimer);
    this.quality = 1;
    this.save();
    this.canvas = null;
    this.wrap = null;
    this.ctx = null;
    this.mounted = false;
  }

  private onWindowResize = () => this.resize();

  /** Render scale, dropped while the camera moves — see `Engine3D.setQuality`. */
  private quality = 1;
  private qualityTimer = 0;

  private setQuality(q: number): void {
    if (this.quality === q) return;
    this.quality = q;
    this.resize();
  }

  private beginInteract(): void {
    window.clearTimeout(this.qualityTimer);
    this.setQuality(0.7);
  }

  private endInteract(): void {
    window.clearTimeout(this.qualityTimer);
    this.qualityTimer = window.setTimeout(() => this.setQuality(1), 150);
  }

  resize(): void {
    const { wrap, canvas } = this;
    if (!wrap || !canvas) return;
    const dpr = (window.devicePixelRatio || 1) * this.quality;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    this.renderer.resize(w, h, dpr);
    this.markDirty();
  }

  private loop = (): void => {
    if (this.dirty) {
      if (this.partDirty) this.rebuild();
      const now = performance.now();
      if (this.lastFrame) {
        const dt = now - this.lastFrame;
        this.fps = this.fps ? this.fps * 0.85 + (1000 / Math.max(1, dt)) * 0.15 : 1000 / Math.max(1, dt);
      }
      this.lastFrame = now;
      this.renderer.render(this.scene, this.camera, {
        ...this.opts,
        scaleMm: this.part.mmPerUnit,
        selected: this.selId ? new Set([this.selId]) : new Set<string>(),
        hoveredId: this.hoverId,
      });
      this.drawSketch();
      this.dirty = false;
      // Deliberately NOT notifying here. A frame is drawn on every step of an
      // orbit, and re-rendering the feature tree and the parameter panel sixty
      // times a second to show nothing new is how a smooth drag turns into a
      // stuttering one. React is told when the DOCUMENT or the selection
      // changes — which is every path that alters what those panels show — and
      // the statistics are polled on their own slow tick.
    } else {
      this.lastFrame = 0;
    }
    this.raf = requestAnimationFrame(this.loop);
  };


  /**
   * Draw the sketch in progress on top of the rendered frame.
   *
   * Painted straight onto the 2D context rather than fed through the rasteriser:
   * it is an annotation in screen space — constant-width lines, handles that
   * stay the size of a cursor at any zoom, and a live length read-out — none of
   * which is geometry, and all of which would be wrong if it scaled with the
   * model.
   */
  private drawSketch(): void {
    const sk = this.sketch;
    const ctx = this.ctx;
    const wrap = this.wrap;
    if (!sk || !ctx || !wrap) return;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    const k = 1 / this.part.mmPerUnit;

    ctx.save();
    ctx.setTransform(this.dprOf(), 0, 0, this.dprOf(), 0, 0);

    /** Sketch millimetres to screen pixels. */
    const scr = (p: Vec2): { x: number; y: number } | null => {
      const w = this.sketchToWorld(p);
      return this.camera.projectPoint(v3(w.x * k, w.y * k, w.z * k), W, H);
    };

    // ── The sketch plane grid ──
    // A plan sketch has the renderer's own floor grid to work against; the two
    // upright planes have nothing, and drawing an outline against a plain sky is
    // guesswork. So they get one, at the snap step so the snapping is visible
    // rather than merely felt.
    if (sk.plane !== 'xz') {
      const step = Math.max(50, this.snapMm || 50) * 4;
      const reach = step * 12;
      ctx.lineWidth = 1;
      for (let i = -12; i <= 12; i++) {
        const d = i * step;
        const major = i % 5 === 0;
        ctx.strokeStyle = major ? 'rgba(40,60,90,0.24)' : 'rgba(40,60,90,0.11)';
        const pairs: [Vec2, Vec2][] = [
          [{ x: d, y: -reach }, { x: d, y: reach }],
          [{ x: -reach, y: d }, { x: reach, y: d }],
        ];
        for (const [a, b] of pairs) {
          const p0 = scr(a);
          const p1 = scr(b);
          if (!p0 || !p1) continue;
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }
      }
    }

    const pts = sk.pts;
    const on = pts.map(scr);
    const closed = sk.op !== 'sweep' && pts.length >= 3;

    // ── The outline so far, plus the rubber band to the cursor ──
    const trace = (upTo: number, extra: { x: number; y: number } | null, close: boolean) => {
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < upTo; i++) {
        const q = on[i];
        if (!q) continue;
        if (!started) {
          ctx.moveTo(q.x, q.y);
          started = true;
        } else ctx.lineTo(q.x, q.y);
      }
      if (extra && started) ctx.lineTo(extra.x, extra.y);
      if (close && started) ctx.closePath();
      ctx.stroke();
    };

    // A closed profile is tinted, so it reads as a face about to become a solid
    // rather than as a loop of lines.
    if (closed) {
      ctx.beginPath();
      let started = false;
      for (const q of on) {
        if (!q) continue;
        if (!started) {
          ctx.moveTo(q.x, q.y);
          started = true;
        } else ctx.lineTo(q.x, q.y);
      }
      if (started) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(43,87,154,0.16)';
        ctx.fill();
      }
    }

    const cursor = sk.preview ? scr(sk.preview) : null;
    ctx.strokeStyle = '#2b579a';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    trace(on.length, null, closed);

    // The band from the last point to the cursor, dashed so it reads as
    // provisional.
    if (cursor && on.length) {
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = 'rgba(43,87,154,0.75)';
      ctx.lineWidth = 1.5;
      const last = on[on.length - 1];
      if (last) {
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(cursor.x, cursor.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // ── Vertices ──
    for (let i = 0; i < on.length; i++) {
      const q = on[i];
      if (!q) continue;
      const isFirst = i === 0;
      const hot = isFirst && sk.closing;
      ctx.beginPath();
      ctx.arc(q.x, q.y, hot ? 6.5 : 4, 0, Math.PI * 2);
      ctx.fillStyle = hot ? '#f26522' : isFirst ? '#1f8a4c' : '#fff';
      ctx.fill();
      ctx.strokeStyle = hot ? '#f26522' : '#2b579a';
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }

    // ── The cursor, and the length and angle of the leg being drawn ──
    if (cursor) {
      ctx.beginPath();
      ctx.arc(cursor.x, cursor.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(43,87,154,0.85)';
      ctx.fill();

      const last = pts[pts.length - 1];
      const label = last
        ? this.legLabel(last, sk.preview!)
        : `${Math.round(sk.preview!.x)}, ${Math.round(sk.preview!.y)} mm`;
      ctx.font = '600 11px "Segoe UI", system-ui, sans-serif';
      const wide = ctx.measureText(label).width + 12;
      const bx = Math.min(W - wide - 4, cursor.x + 12);
      const by = Math.max(20, cursor.y - 12);
      ctx.fillStyle = 'rgba(24,34,48,0.86)';
      ctx.fillRect(bx, by - 14, wide, 19);
      ctx.fillStyle = '#f2f6fb';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, bx + 6, by - 4);
    }

    ctx.restore();
  }

  /** Length and bearing of the leg from `a` to `b`, as the read-out shows it. */
  private legLabel(a: Vec2, b: Vec2): string {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    // Measured off the sketch's horizontal axis, the way a fabricator would set
    // it out, and normalised so a leg drawn leftward reads 180 rather than -180.
    let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (ang < 0) ang += 360;
    const txt = len >= 1000 ? `${(len / 1000).toFixed(3).replace(/\.?0+$/, '')} m` : `${Math.round(len)} mm`;
    return `${txt}  ∠${ang.toFixed(0)}°`;
  }

  /** The device pixel ratio the canvas is currently backed at. */
  private dprOf(): number {
    return (window.devicePixelRatio || 1) * this.quality;
  }

  private markDirty(): void {
    this.dirty = true;
  }

  /** Mark the evaluated part stale — it rebuilds before the next frame. */
  invalidate(): void {
    this.partDirty = true;
    this.dirty = true;
  }

  private rebuild(): void {
    const built = buildPart(this.part);
    this.scene = built.scene;
    this.stats = built.stats;
    this.partDirty = false;
  }

  onFrame(fn: SolidListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  // ── Document ───────────────────────────────────────────────────────────

  getPart(): Part {
    return this.part;
  }

  getStats(): SolidStats {
    return { ...this.stats, fps: Math.round(this.fps) };
  }

  getSelectedId(): string | null {
    return this.selId;
  }

  getSelected(): Feature | null {
    return this.part.features.find((f) => f.id === this.selId) ?? null;
  }

  select(id: string | null): void {
    if (this.selId === id) return;
    this.selId = id;
    this.markDirty();
    this.notify();
  }

  setPartName(name: string): void {
    this.part.name = name;
    this.notify();
  }

  setScale(mmPerUnit: number): void {
    this.pushUndo();
    this.part.mmPerUnit = Math.max(1, mmPerUnit);
    this.invalidate();
  }

  /**
   * Snapshot for undo. `key` coalesces a burst of edits to the same thing —
   * dragging a slider is one undo step, not one per pointer event.
   */
  private pushUndo(key = ''): void {
    if (key && key === this.coalesceKey) return;
    this.undoStack.push(JSON.stringify(this.part));
    if (this.undoStack.length > 80) this.undoStack.shift();
    this.redoStack.length = 0;
    this.coalesceKey = key;
    if (key) {
      window.clearTimeout(this.coalesceTimer);
      // Long enough to cover a continuous drag, short enough that two separate
      // edits to the same field stay separately undoable.
      this.coalesceTimer = window.setTimeout(() => (this.coalesceKey = ''), 700);
    }
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): void {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(JSON.stringify(this.part));
    this.part = JSON.parse(prev) as Part;
    this.coalesceKey = '';
    if (!this.part.features.some((f) => f.id === this.selId)) this.selId = null;
    this.invalidate();
    this.notify();
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(JSON.stringify(this.part));
    this.part = JSON.parse(next) as Part;
    this.coalesceKey = '';
    if (!this.part.features.some((f) => f.id === this.selId)) this.selId = null;
    this.invalidate();
    this.notify();
  }

  // ── Feature editing ────────────────────────────────────────────────────

  addFeature(kind: FeatureKind): void {
    this.pushUndo();
    const f = mkFeature(kind, { name: uniqueName(this.part, KINDS[kind].label) });
    // Drop it clear of what is already there rather than inside it, so a new
    // feature is always visible the moment it is added. The FIRST feature goes
    // on the origin, where the part's own datum is.
    if (this.part.features.length) {
      const k = this.part.mmPerUnit;
      f.x = Math.round((this.scene.max.x * k + 400) / 50) * 50;
    }
    const wasEmpty = !this.part.features.length;
    this.part.features.push(f);
    this.selId = f.id;
    this.invalidate();
    // The empty page is framed on a placeholder box, so the first feature would
    // otherwise arrive part-way out of shot.
    if (wasEmpty) this.fitView();
    this.notify();
  }

  /** Add a template's features to the current part, keeping what is there. */
  insertTemplate(id: string): void {
    const p = partFromTemplate(id);
    if (!p) return;
    this.pushUndo();
    const k = this.part.mmPerUnit;
    const wasEmpty = !this.part.features.length;
    const dx = wasEmpty ? 0 : Math.round((this.scene.max.x * k + 900) / 100) * 100;
    for (const f of p.features) {
      f.name = uniqueName(this.part, f.name);
      f.x += dx;
      this.part.features.push(f);
    }
    this.selId = p.features[0]?.id ?? null;
    syncSeq(this.part);
    this.invalidate();
    if (wasEmpty) this.fitView();
    this.notify();
  }

  /** Replace the whole part with a template. */
  loadTemplate(id: string, undoable = true): void {
    const p = partFromTemplate(id);
    if (!p) return;
    if (undoable) this.pushUndo();
    this.part = p;
    syncSeq(this.part);
    this.selId = p.features[0]?.id ?? null;
    this.invalidate();
    this.fitView();
    this.notify();
  }

  newPart(): void {
    this.pushUndo();
    this.part = emptyPart();
    this.selId = null;
    // Clear the autosave too, or a reload would bring back the part that was
    // just discarded. Undo still has it for as long as the session lasts.
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
    } catch {
      // A blocked localStorage must never break the modeller.
    }
    this.invalidate();
    this.notify();
  }

  /** True when there is nothing modelled — drives the empty-page placeholder. */
  isEmpty(): boolean {
    return this.part.features.length === 0;
  }

  private withFeature(id: string, fn: (f: Feature) => void, undoKey = ''): void {
    const f = this.part.features.find((q) => q.id === id);
    if (!f) return;
    this.pushUndo(undoKey ? id + ':' + undoKey : '');
    fn(f);
    this.invalidate();
    this.notify();
  }

  setParam(id: string, key: string, value: number): void {
    this.withFeature(id, (f) => (f.p[key] = value), 'p.' + key);
  }

  setPlacement(id: string, key: 'x' | 'y' | 'z' | 'rx' | 'ry' | 'rz', value: number): void {
    this.withFeature(id, (f) => (f[key] = value), 'pl.' + key);
  }

  setMaterial(id: string, mat: MatKey): void {
    this.withFeature(id, (f) => (f.mat = mat));
  }

  rename(id: string, name: string): void {
    this.withFeature(id, (f) => (f.name = name), 'name');
  }

  toggleSuppressed(id: string): void {
    this.withFeature(id, (f) => (f.suppressed = !f.suppressed));
  }

  duplicate(id: string): void {
    const i = this.part.features.findIndex((f) => f.id === id);
    if (i < 0) return;
    this.pushUndo();
    const src = this.part.features[i];
    const copy: Feature = {
      ...(JSON.parse(JSON.stringify(src)) as Feature),
      id: nextId(),
      name: uniqueName(this.part, src.name),
    };
    // Offset the copy so it is not hidden exactly inside the original.
    copy.x += 200;
    this.part.features.splice(i + 1, 0, copy);
    this.selId = copy.id;
    this.invalidate();
    this.notify();
  }

  remove(id: string): void {
    const i = this.part.features.findIndex((f) => f.id === id);
    if (i < 0) return;
    this.pushUndo();
    this.part.features.splice(i, 1);
    if (this.selId === id) this.selId = this.part.features[Math.min(i, this.part.features.length - 1)]?.id ?? null;
    this.invalidate();
    this.notify();
  }

  /** Move a feature up or down the tree. Order matters only for legibility. */
  reorder(id: string, dir: -1 | 1): void {
    const i = this.part.features.findIndex((f) => f.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= this.part.features.length) return;
    this.pushUndo();
    const [f] = this.part.features.splice(i, 1);
    this.part.features.splice(j, 0, f);
    this.invalidate();
    this.notify();
  }

  setPattern(id: string, patt: Pattern | undefined): void {
    this.withFeature(id, (f) => {
      if (patt) f.pattern = patt;
      else delete f.pattern;
    });
  }

  updatePattern(id: string, over: Partial<Pattern>): void {
    this.withFeature(
      id,
      (f) => {
        const base: Pattern = f.pattern ?? { kind: 'linear', count: 2, spacing: 500, axis: 'x' };
        f.pattern = { ...base, ...over };
      },
      'patt',
    );
  }

  /** Drop the selected feature onto y = 0 — the commonest placement fix. */
  dropToFloor(id: string): void {
    const ob = this.scene.objects.find((o) => o.id === id);
    if (!ob) return;
    const k = this.part.mmPerUnit;
    this.withFeature(id, (f) => (f.y = Math.round(f.y - ob.min.y * k)));
  }

  /** Centre the selected feature on the part's X/Z axis. */
  centreOnAxis(id: string): void {
    this.withFeature(id, (f) => {
      f.x = 0;
      f.z = 0;
    });
  }


  // ── Sketching ──────────────────────────────────────────────────────────
  //
  // The escape hatch from the catalogue: draw the outline the job needs, then
  // extrude, revolve or sweep it. Points are collected in millimetres on the
  // sketch plane and stored on the feature, so the profile stays editable for as
  // long as the part exists — a sketch is a parametric feature like any other,
  // not a one-off mesh baked at the moment of drawing.

  getSketch(): SketchState | null {
    return this.sketch;
  }

  isSketching(): boolean {
    return !!this.sketch;
  }

  /** Start a new sketch, and look straight at its plane. */
  beginSketch(plane: Sketch['plane'], op: Sketch['op']): void {
    // Revolving turns the horizontal ordinate into a radius about the Y axis,
    // which only reads as a profile on an upright plane.
    const pl = op === 'revolve' ? 'xy' : plane;
    this.sketch = {
      plane: pl,
      op,
      pts: [],
      editing: null,
      origin: v3(0, 0, 0),
      preview: null,
      closing: false,
    };
    this.selId = null;
    this.faceOn();
    this.notify();
  }

  /** Re-open an existing sketch feature for editing, in place. */
  editSketch(id: string): void {
    const f = this.part.features.find((q) => q.id === id);
    if (!f || !f.sketch) return;
    this.sketch = {
      plane: f.sketch.plane,
      op: f.sketch.op,
      pts: f.sketch.pts.map((q) => ({ x: q.x, y: q.y })),
      editing: id,
      // Drawn against the feature's own placement, so the vertices come back
      // under the cursor rather than at the world origin.
      origin: v3(f.x, f.y, f.z),
      preview: null,
      closing: false,
    };
    this.selId = id;
    this.faceOn();
    this.notify();
  }

  /** Point the camera square at the sketch plane, in orthographic. */
  private faceOn(): void {
    if (!this.sketch) return;
    // Square on to the plane, not the view presets' few degrees of tilt: those
    // exist so a plant reads as a solid, and here they would put every point a
    // little to one side of where it looks. Plan keeps half a degree because at
    // exactly ninety the up vector degenerates.
    const pl = this.sketch.plane;
    this.camera.azimuth = pl === 'zy' ? deg(-90) : 0;
    this.camera.elevation = pl === 'xz' ? deg(89.5) : 0;
    // Perspective foreshortening, on a plane you are drawing a dimensioned
    // outline on, is actively misleading.
    this.camera.kind = 'ortho';
    if (this.sketch.pts.length) this.fitSketch();
    else this.fitView();
    this.markDirty();
  }

  /** Frame the points drawn so far. */
  private fitSketch(): void {
    const sk = this.sketch;
    if (!sk || !sk.pts.length) return;
    const k = 1 / this.part.mmPerUnit;
    let ax = Infinity;
    let ay = Infinity;
    let az = Infinity;
    let bx = -Infinity;
    let by = -Infinity;
    let bz = -Infinity;
    for (const q of sk.pts) {
      const w = this.sketchToWorld(q);
      ax = Math.min(ax, w.x * k);
      ay = Math.min(ay, w.y * k);
      az = Math.min(az, w.z * k);
      bx = Math.max(bx, w.x * k);
      by = Math.max(by, w.y * k);
      bz = Math.max(bz, w.z * k);
    }
    const pad = 250 * k;
    this.camera.fit(
      v3(ax - pad, ay - pad, az - pad),
      v3(bx + pad, by + pad, bz + pad),
      (this.wrap?.clientWidth || 800) / (this.wrap?.clientHeight || 600),
      1.1,
    );
    this.markDirty();
  }

  /** Sketch-plane millimetres to world millimetres. */
  private sketchToWorld(p: Vec2): Vec3 {
    const sk = this.sketch;
    if (!sk) return v3();
    const o = sk.origin;
    if (sk.plane === 'xz') return v3(o.x + p.x, o.y, o.z + p.y);
    if (sk.plane === 'xy') return v3(o.x + p.x, o.y + p.y, o.z);
    return v3(o.x, o.y + p.y, o.z + p.x);
  }

  /** Where the cursor meets the sketch plane, in sketch millimetres. */
  private sketchAt(px: number, py: number): Vec2 | null {
    const sk = this.sketch;
    const canvas = this.canvas;
    if (!sk || !canvas) return null;
    const k = this.part.mmPerUnit;
    const ray = this.camera.rayFrom(px, py, canvas.clientWidth, canvas.clientHeight);
    const n = sk.plane === 'xz' ? v3(0, 1, 0) : sk.plane === 'xy' ? v3(0, 0, 1) : v3(1, 0, 0);
    const at = v3(sk.origin.x / k, sk.origin.y / k, sk.origin.z / k);
    const hit = rayPlanePoint(ray.o, ray.d, at, n);
    if (!hit) return null;
    const wx = hit.x * k - sk.origin.x;
    const wy = hit.y * k - sk.origin.y;
    const wz = hit.z * k - sk.origin.z;
    const raw =
      sk.plane === 'xz' ? { x: wx, y: wz } : sk.plane === 'xy' ? { x: wx, y: wy } : { x: wz, y: wy };
    const snap = (v: number) =>
      this.snapMm > 0 ? Math.round(v / this.snapMm) * this.snapMm : Math.round(v);
    return { x: snap(raw.x), y: snap(raw.y) };
  }

  /** Add a vertex. Clicking the first one again closes the outline. */
  private addSketchPoint(px: number, py: number): void {
    const sk = this.sketch;
    const p = this.sketchAt(px, py);
    if (!sk || !p) return;
    if (sk.closing && sk.pts.length >= 3) {
      this.finishSketch();
      return;
    }
    const last = sk.pts[sk.pts.length - 1];
    // Ignore a second click landing on the same snapped point.
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < 1e-6) return;
    sk.pts.push(p);
    this.markDirty();
    this.notify();
  }

  /** Drop the last vertex. */
  undoSketchPoint(): void {
    if (!this.sketch) return;
    this.sketch.pts.pop();
    this.markDirty();
    this.notify();
  }

  /** How many points this operation needs before it can make a solid. */
  sketchReady(): boolean {
    const sk = this.sketch;
    if (!sk) return false;
    return sk.pts.length >= (sk.op === 'sweep' ? 2 : 3);
  }

  cancelSketch(): void {
    if (!this.sketch) return;
    this.sketch = null;
    this.camera.preset('isoSW');
    this.fitView();
    this.notify();
  }

  /** Commit the sketch as a feature — a new one, or the one being edited. */
  finishSketch(): void {
    const sk = this.sketch;
    if (!sk) return;
    if (!this.sketchReady()) {
      toast(
        sk.op === 'sweep'
          ? 'A swept duct needs at least two points'
          : 'A profile needs at least three points',
      );
      return;
    }
    this.pushUndo();
    const data: Sketch = {
      plane: sk.plane,
      op: sk.op,
      pts: sk.pts.map((q) => ({ x: q.x, y: q.y })),
    };

    if (sk.editing) {
      const f = this.part.features.find((q) => q.id === sk.editing);
      if (f) f.sketch = data;
    } else {
      const label =
        sk.op === 'extrude' ? 'Profile' : sk.op === 'revolve' ? 'Revolved profile' : 'Swept duct';
      const f = mkFeature('sketch', { name: uniqueName(this.part, label), sketch: data });
      f.x = sk.origin.x;
      f.y = sk.origin.y;
      f.z = sk.origin.z;
      this.part.features.push(f);
      this.selId = f.id;
    }
    this.sketch = null;
    this.invalidate();
    // Back to a three-quarter view: the point of finishing is to see the solid,
    // not the outline it came from.
    this.camera.preset('isoSW');
    this.fitView();
    this.notify();
  }

  /** Change the operation mid-sketch, keeping the points already drawn. */
  setSketchOp(op: Sketch['op']): void {
    const sk = this.sketch;
    if (!sk) return;
    sk.op = op;
    if (op === 'revolve' && sk.plane !== 'xy') {
      sk.plane = 'xy';
      this.faceOn();
    }
    this.markDirty();
    this.notify();
  }

  /** Change the sketch plane, keeping the points as drawn on the new one. */
  setSketchPlane(plane: Sketch['plane']): void {
    const sk = this.sketch;
    if (!sk || sk.op === 'revolve') return;
    sk.plane = plane;
    this.faceOn();
    this.notify();
  }

  // ── View ───────────────────────────────────────────────────────────────

  setPreset(p: ViewPreset): void {
    this.camera.preset(p);
    this.fitView();
  }

  getProjection(): ProjectionKind {
    return this.camera.kind;
  }

  setProjection(k: ProjectionKind): void {
    this.camera.kind = k;
    this.markDirty();
  }

  toggle(k: 'grid' | 'shadows' | 'labels' | 'isolate'): void {
    this.opts[k] = !this.opts[k];
    this.markDirty();
  }

  fitView(): void {
    if (this.partDirty) this.rebuild();
    const w = this.wrap?.clientWidth || 800;
    const h = this.wrap?.clientHeight || 600;
    if (this.scene.count > 0)
      this.camera.fitPoints(this.scene.pos, this.scene.count * 3, this.centre(), w / h, 1);
    else this.camera.fit(v3(-20, 0, -20), v3(20, 20, 20), w / h);
    this.markDirty();
  }

  /** Frame just the selected feature — the CAD "zoom to selection". */
  fitSelection(): void {
    const ob = this.scene.objects.find((o) => o.id === this.selId);
    if (!ob) return this.fitView();
    const w = this.wrap?.clientWidth || 800;
    const h = this.wrap?.clientHeight || 600;
    this.camera.fit(ob.min, ob.max, w / h, 1.25);
    this.markDirty();
  }

  private centre(): Vec3 {
    const s = this.scene;
    return v3((s.min.x + s.max.x) / 2, (s.min.y + s.max.y) / 2, (s.min.z + s.max.z) / 2);
  }

  // ── Input ──────────────────────────────────────────────────────────────

  private attachInput(canvas: HTMLCanvasElement): void {
    let mode: 'none' | 'orbit' | 'pan' | 'move' = 'none';
    let lx = 0;
    let ly = 0;
    /** Grab offset so a dragged feature does not jump to the cursor. */
    let grab: { x: number; z: number; y: number } | null = null;
    let moved = false;

    const local = (e: PointerEvent | WheelEvent | MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    /** Where the cursor meets the plane the drag is constrained to. */
    const planePoint = (px: number, py: number, vertical: boolean): Vec3 | null => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const ray = this.camera.rayFrom(px, py, w, h);
      if (!vertical) return rayPlanePoint(ray.o, ray.d, v3(0, grab?.y ?? 0, 0), v3(0, 1, 0));
      // Vertical drags run on the plane facing the camera, so the feature
      // tracks the cursor rather than sliding away from it.
      const f = this.camera.forward();
      const n = v3(f.x, 0, f.z);
      const at = v3(grab?.x ?? 0, 0, grab?.z ?? 0);
      return rayPlanePoint(ray.o, ray.d, at, n);
    };

    const snap = (v: number): number =>
      this.snapMm > 0 ? Math.round(v / this.snapMm) * this.snapMm : Math.round(v);

    const onDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      const { x, y } = local(e);
      lx = x;
      ly = y;
      moved = false;
      this.beginInteract();

      // Middle button or space-style modifier pans; right button orbits, as in
      // the plant view.
      if (e.button === 1 || (e.button === 0 && e.shiftKey && e.altKey)) {
        mode = 'pan';
        return;
      }
      if (e.button === 2) {
        mode = 'pan';
        return;
      }

      // While a sketch is open the left button lays down vertices. Panning and
      // zooming still work; orbiting does not, because a profile drawn from a
      // viewpoint that is not square to its plane is not the profile the user
      // thinks they drew.
      if (this.sketch) {
        if (e.button === 0) {
          this.addSketchPoint(x, y);
          mode = 'none';
        }
        return;
      }

      const hit = this.renderer.pick(x, y, this.scene);
      if (e.button === 0 && hit && hit.id === this.selId) {
        // Clicking the already-selected feature starts a move. Selecting first
        // and moving second is what stops an orbit from nudging the part.
        const f = this.getSelected();
        if (f) {
          const k = this.part.mmPerUnit;
          grab = { x: f.x, y: (hit.min.y + hit.max.y) / 2, z: f.z };
          const p = planePoint(x, y, e.shiftKey);
          if (p) {
            grab = {
              x: f.x - p.x * k,
              y: (hit.min.y + hit.max.y) / 2,
              z: f.z - p.z * k,
            };
            mode = 'move';
            return;
          }
        }
      }
      if (e.button === 0) {
        this.select(hit ? hit.id : null);
        mode = 'orbit';
        return;
      }
      mode = 'orbit';
    };

    const onMove = (e: PointerEvent) => {
      const { x, y } = local(e);
      const dx = x - lx;
      const dy = y - ly;
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) moved = true;

      if (mode === 'none' && this.sketch) {
        const sk = this.sketch;
        const p = this.sketchAt(x, y);
        const first = sk.pts[0];
        // "Near the first point" is judged in PIXELS, not millimetres: at any
        // zoom the target has to be the size of a cursor, not the size of a
        // snap step.
        let closing = false;
        if (first && sk.pts.length >= 3 && sk.op !== 'sweep') {
          const w = this.sketchToWorld(first);
          const k = 1 / this.part.mmPerUnit;
          const q = this.camera.projectPoint(
            v3(w.x * k, w.y * k, w.z * k),
            canvas.clientWidth,
            canvas.clientHeight,
          );
          if (q) closing = Math.hypot(q.x - x, q.y - y) < 11;
        }
        if (
          closing !== sk.closing ||
          !sk.preview ||
          !p ||
          p.x !== sk.preview.x ||
          p.y !== sk.preview.y
        ) {
          sk.preview = p;
          sk.closing = closing;
          this.markDirty();
        }
        return;
      }

      if (mode === 'none') {
        const hit = this.renderer.pick(x, y, this.scene);
        const id = hit?.id ?? null;
        if (id !== this.hoverId) {
          this.hoverId = id;
          this.markDirty();
        }
        return;
      }

      lx = x;
      ly = y;
      if (mode === 'orbit') {
        this.camera.orbit(-dx * 0.0072, -dy * 0.0062);
        this.markDirty();
      } else if (mode === 'pan') {
        this.camera.pan(dx, dy, canvas.clientHeight);
        this.markDirty();
      } else if (mode === 'move' && grab && this.selId) {
        const k = this.part.mmPerUnit;
        const vertical = e.shiftKey;
        const p = planePoint(x, y, vertical);
        if (!p) return;
        const id = this.selId;
        this.withFeature(
          id,
          (f) => {
            if (vertical) {
              // Only the height changes on a vertical drag; the plan position
              // is what the user already set.
              f.y = snap(f.y + (p.y * k - (grab?.y ?? 0) * k));
              grab = { x: grab!.x, y: p.y, z: grab!.z };
            } else {
              f.x = snap(p.x * k + grab!.x);
              f.z = snap(p.z * k + grab!.z);
            }
          },
          'drag',
        );
      }
    };

    const onUp = (e: PointerEvent) => {
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      // A click that never moved and hit nothing clears the selection.
      if (mode === 'orbit' && !moved) {
        const { x, y } = local(e);
        const hit = this.renderer.pick(x, y, this.scene);
        this.select(hit ? hit.id : null);
      }
      mode = 'none';
      grab = null;
      this.endInteract();
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x, y } = local(e);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const factor = wheelZoomFactor(e.deltaY, e.deltaMode);
      // Zoom toward whatever is under the cursor, falling back to the ground
      // plane where there is nothing — the same rule as the plant view.
      const depth = this.renderer.pickDepth(x, y);
      const ray = this.camera.rayFrom(x, y, w, h);
      let at: Vec3 | null = null;
      if (depth !== null) {
        // `depth` is measured along the VIEW AXIS, while the ray is a unit
        // vector that may be well off it — stepping by the raw figure would
        // anchor the zoom short of the surface near the edges of the viewport.
        const fwd = this.camera.forward();
        const along = Math.max(0.2, ray.d.x * fwd.x + ray.d.y * fwd.y + ray.d.z * fwd.z);
        const t = depth / along;
        at = v3(ray.o.x + ray.d.x * t, ray.o.y + ray.d.y * t, ray.o.z + ray.d.z * t);
      } else {
        at = rayPlanePoint(ray.o, ray.d, v3(0, 0, 0), v3(0, 1, 0));
      }
      if (at) this.camera.zoomAt(at, factor);
      else this.camera.dolly(factor);
      this.beginInteract();
      this.endInteract();
      this.markDirty();
    };

    const onDbl = (e: MouseEvent) => {
      if (this.sketch) {
        this.finishSketch();
        return;
      }
      const { x, y } = local(e);
      const hit = this.renderer.pick(x, y, this.scene);
      if (hit) {
        this.select(hit.id);
        this.fitSelection();
      } else this.fitView();
    };

    const onContext = (e: Event) => e.preventDefault();

    const onKey = (e: KeyboardEvent) => {
      // Never steal keys from a field the user is typing in.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
      const step = e.shiftKey ? (this.snapMm || 10) * 4 : this.snapMm || 10;
      const nudge = (key: 'x' | 'y' | 'z', d: number) => {
        if (!this.selId) return;
        e.preventDefault();
        const id = this.selId;
        this.withFeature(id, (f) => (f[key] = Math.round(f[key] + d)), 'nudge');
      };
      // Sketch keys come first and are exclusive: Delete must not reach the
      // feature it would otherwise erase while an outline is being drawn.
      if (this.sketch) {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.finishSketch();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          this.cancelSketch();
          return;
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          this.undoSketchPoint();
          return;
        }
      }

      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) this.redo();
        else this.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        this.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (this.selId) this.duplicate(this.selId);
        return;
      }
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (this.selId) {
            e.preventDefault();
            this.remove(this.selId);
          }
          break;
        case 'Escape':
          this.select(null);
          break;
        case 'f':
        case 'F':
          if (this.selId) this.fitSelection();
          else this.fitView();
          break;
        case 'ArrowLeft':
          nudge('x', -step);
          break;
        case 'ArrowRight':
          nudge('x', step);
          break;
        case 'ArrowUp':
          nudge(e.altKey ? 'y' : 'z', e.altKey ? step : -step);
          break;
        case 'ArrowDown':
          nudge(e.altKey ? 'y' : 'z', e.altKey ? -step : step);
          break;
        default:
          break;
      }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('dblclick', onDbl);
    canvas.addEventListener('contextmenu', onContext);
    window.addEventListener('keydown', onKey);

    this.detachers.push(() => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('dblclick', onDbl);
      canvas.removeEventListener('contextmenu', onContext);
      window.removeEventListener('keydown', onKey);
    });
  }

  // ── Persistence and export ─────────────────────────────────────────────

  private save(): void {
    // Nothing to remember about an empty page, and writing one would overwrite
    // the previous session's work on the way out of a mode the user only opened
    // to look at. `newPart` clears the key explicitly when that is the intent.
    if (!this.part.features.length) return;
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(this.part));
    } catch {
      // A full or blocked localStorage must never break the modeller.
    }
  }

  private restore(): boolean {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (!raw) return false;
      const p = JSON.parse(raw) as Part;
      if (!p || !Array.isArray(p.features) || !p.features.length) return false;
      this.part = p;
      syncSeq(this.part);
      return true;
    } catch {
      return false;
    }
  }

  /** Save the part as JSON — the editable format. */
  exportJson(): void {
    this.save();
    const name = (this.part.name || 'part').replace(/[^\w\- ]+/g, '').trim() || 'part';
    dlText(JSON.stringify(this.part, null, 2), `${name}.part.json`, 'application/json');
    toast(`Saved ${name}.part.json`);
  }

  async importJson(file: File): Promise<void> {
    try {
      const p = JSON.parse(await file.text()) as Part;
      if (!p || !Array.isArray(p.features)) throw new Error('not a part file');
      this.pushUndo();
      this.part = { name: p.name || 'Imported part', mmPerUnit: p.mmPerUnit || 25, features: p.features };
      syncSeq(this.part);
      this.selId = this.part.features[0]?.id ?? null;
      this.invalidate();
      this.fitView();
      this.notify();
      toast(`Loaded ${this.part.name}`);
    } catch {
      toast('That file is not a part — expected a .part.json');
    }
  }

  /** Export the evaluated solid as a binary STL, in millimetres. */
  exportStl(): void {
    if (this.partDirty) this.rebuild();
    if (!this.scene.count) {
      toast('Nothing to export yet');
      return;
    }
    const name = (this.part.name || 'part').replace(/[^\w\- ]+/g, '').trim() || 'part';
    dlBlob(sceneToStl(this.scene, name, this.part.mmPerUnit), `${name}.stl`);
    toast(`Exported ${name}.stl — ${this.scene.count.toLocaleString()} facets, in mm`);
  }

  /** Snapshot the viewport as a PNG. */
  exportPng(): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const name = (this.part.name || 'part').replace(/[^\w\- ]+/g, '').trim() || 'part';
    canvas.toBlob((b) => {
      if (b) dlBlob(b, `${name}.png`);
    }, 'image/png');
  }
}

/** The single instance the Solid view mounts. */
export const solidEngine = new SolidEngine();
